import { Subject } from 'rxjs';


// This file doesn't contain the most modern of practices, because it
// was essentially transliterated from an old jsQTI enyo component to
// modern class/module syntax. Events are published using observables.


let speechStreamSingleton = undefined,
    loader = undefined,
    requestedYet = false,
    requests = [];

const resolveRequests = () => {
  requests.forEach( callback => {
    callback( speechStreamSingleton );
  });

  requests = [];
};

const toolbarButtonsByFeatures = {
  tts: ['hover', 'play', 'pause', 'stop'],
  dictionary: ['stop', 'dictionary'],
  pictureDictionary: ['stop', 'picturedict'],
}

const sspcIgnoreList = '.lrn_contentWrapper[aria-hidden="true"]:not(.lrn-possible-answer .lrn_contentWrapper), .lrn-possible-answer .sr-only, #app_bubbleSheet_control, #app_bubbleSheet_SubmitAnswerModalAtEnd_submitTestButtonLabel, .prev-next-button .mq-math-mode, .lrn_choiceLabel, [class^=lrn-accessibility-], .test-title-text, .subtitle, .item-count, .timer, .lrn_sort_gripper, .lrn-choicematrix-column-title, .footer, .lrn_btn, .lrn-assess-skip-to-link, .lrn-assess-skip-to-content-link, .lrn_contentWrapper img[title]:not([alt]), .lrn_contentWrapper img[title][alt=""], .lrn-toolbar-footer, [class^=lrn-annotations-], .lrn_response > .sr-only, .lrn_possibilityList .sr-only, .lrn_dropzone_mask > .sr-only, .lrn_cloze_response_container .sr-only, .lrn_sortlist_column_wrapper .sr-only, .lrn_input_wrapper > .sr-only, .lrn_token > .sr-only'

const requestSpeechStream = (callback, toolbarConfig) => {
  // Request SpeechStream Toolbar
  requests.push(callback);

  if (!requestedYet) {
    if (typeof loader !== 'undefined') {
      requestedYet = true;

      const configuredButtons = Object.entries(toolbarConfig)
          // Removing disabled features
          .filter(config => config[1])
          // Creating buttons array
          .flatMap(config => toolbarButtonsByFeatures[config[0]])
          // Removing duplicate buttons
          .filter((button, index, arr) => arr.indexOf(button) === index)

      loader.setToolBarButtons(configuredButtons)

      loader.lateLoad()
          .then((api) => {
            speechStreamSingleton = api;

            // Required addition for v3. False param indicates that the value will be appended instead of overwritten.
            speechStreamSingleton.domControlTools.setBreakList('!x-marker', false);

            var sspc = speechStreamSingleton.domControlTools.getNewDomControl(window.document);
            sspc.setIgnoreAll(true);
            sspc.addIgnoreListQuerySelector(sspcIgnoreList);
            // Resolve any callbacks stored in requests.
            resolveRequests();
          });
    } else {
      callback(null);  // TextHelp script not loaded on page for some reason.
      requests.pop();  // Remove failed callback that we just barely pushed.
    }
  } else if (speechStreamSingleton) {
    resolveRequests();
  }
};

export const EVENTS = {

  // These events are raised after setting `enabled` true for the
  // first time. At most one will be called. Loading will fail
  // when the TextHelp script could not be fetched, or is not
  // available in this context.
  LOADING_FINISHED: 'STANDARD_ORATOR_LOADING_FINISHED',
  LOADING_FAILED: 'STANDARD_ORATOR_LOADING_FAILED',

  // This event is raised when the orator has become enabled and
  // SpeechStream is ready. It is raised every time `enabled` becomes
  // true, but *only* when the orator is actually ready for use. It's
  // always raised directly after LOADING_FINISHED. It will never be
  // called if LOADING_FAILED was raised.
  BECAME_AVAILABLE: 'STANDARD_ORATOR_BECAME_AVAILABLE',
};

class StandardOratorClass {

  // For classical contexts where it's non-trivial to import the
  // EVENTS constant:
  events = EVENTS;


  // Public properties
  //
  // The getters are listed up front; these are settable as well using
  // standard javascript syntax, unless marked as read-only. If you're
  // coming from an enyo background, please don't use explicit
  // `setXyz()` methods anymore, just do `.xyz =`


  // `enabled`: Whether the toolbar should be loaded and available
  // for the current session. Default is false. Enabling is
  // asynchronous and may fail---see published events.
  get enabled () {
    return this._enabled;
  }

  // Whether or not a passage should be able to be read by the toolbar.
  // Default is true, this is set when setting speakable node
  // and used when handling speakable node changes.
  get includePassage () {
    return this._includePassage;
  }

  // Whether the toolbar should be visible. Default is false. This
  // value is a request: If `enabled` is false, it is not used; if
  // the toolbar's loading is pending, it will become visible as
  // soon as possible; if loading fails, it is inoperative.
  get visible () {
    return this._visible;
  }

  // The [book_id, page_id] id for the readable node. Changing
  // this forces reading to stop.
  get bookAndPage () {
    return this._bookAndPage;
  }

  // The item on the page that's currently readable. Changing this
  // forces reading to stop.
  get readableNode () {
    return this._readableNode;
  }

  // READ-ONLY: An RxJS Observable of actions (in Redux format) that
  // you can subscribe to.
  get action$ () {
    return this._action$_observable;
  }

  // THESE ARE INTERNAL AND SHOULD NOT BE ACCESSED OUTSIDE THE GETTERS AND SETTERS:
  _enabled = false;
  _includePassage = true;
  _visible = false;
  _bookAndPage = null;
  _readableNode = null;
  _oldReadableNode = null;
  _nodesChanged = false;
  _action$ = new Subject();
  _action$_observable = this._action$.asObservable();
  _toolbarConfig = {};
  // THANK YOU.

  // Private properties:

  // Whether SpeechStream is ready to go:
  serviceReady = false;

  // Stop reading. Does nothing unless SpeechStream is available and
  // currently speaking.
  stop () {
    if (this.serviceReady && this._toolbar) {
      this._toolbar.speechTools.stop();
    }
  }

  set readableNode (rn) {

    this._oldReadableNode = this._readableNode;
    this._readableNode = rn;

    this.stop();

    if (this.serviceReady) {
      // If TTS is ready, proceed with enabling nodes, otherwise nodes will be handled once TTS has been loaded.
      this._handleNodeChange();
    } else {
      this._nodesChanged = true;
    }
  }

  set bookAndPage (bap) {
    this._bookAndPage = bap;

    this.stop();

    if (!this.enabled || !this.serviceReady) {
      // If the service isn't ready, don't allow the value to stick:
      this._bookAndPage = null;
      return;
    }

    var book = null, page = null;
    if (bap && bap.length === 2) {
      book = bap[0];
      page = bap[1];
    }

    this._toolbar.speechTools.setSpeechBookAndPageID(book, page);
  }

  set toolbarConfig (config) {
    this._toolbarConfig = config;
  }

  set enabled (ne) {
    this._enabled = !!ne;

    if (this.enabled && this._toolbar == undefined) {
      // Toolbar has not been loaded yet.
      requestSpeechStream( this._speechStreamReady.bind(this), this._toolbarConfig );
    } else if (this.enabled && this.visible) {
      // Already loaded. Visibility left on while disabled.
      this._setVisibility(true);
      this._dispatch({ type: EVENTS.BECAME_AVAILABLE });
    } else {
      // Make invisible, but leave value true.
      this._setVisibility(false);
    }
  }

  set includePassage (ip) {
    this._includePassage = !!ip;
  }

  set visible (v) {
    this._visible = v;

    if (this.enabled) {
      this._setVisibility(this.visible);
    }
  }

  _speechStreamReady (toolbar) {

    if (toolbar) {

      this._toolbar = toolbar;
      this.serviceReady = true;

      if(this._nodesChanged) {
        // Node enablment was delayed. Handle now.
        this._handleNodeChange();
      }

      if (this.visible) {
        // Loaded for the first time, but visibility was already
        // requested (and thus delayed).
        this._setVisibility(true);
      }

      this._dispatch({ type: EVENTS.LOADING_FINISHED });
      this._dispatch({ type: EVENTS.BECAME_AVAILABLE });

    } else {

      this._dispatch({ type: EVENTS.LOADING_FAILED });

    }
  }

  _setVisibility (vis) {

    if (!this._toolbar) {
      // After loading failure, all changes become noöps.
      return;
    }

    if(this._toolbar.isToolbarActive() !== vis) {
      this._toolbar.toggleBar();
    }

    if (!vis) {  // Now hiding, so stop speech.
      this.stop();
    }
  }

  _dispatch (action) {
    this._action$.next(action);
  }

  _handleNodeChange () {

    this._nodesChanged = false;

    var sspc = speechStreamSingleton.domControlTools.getNewDomControl(window.document);

    // For some reason the "aria-label"-s and data-ssml attributes on input and textarea nodes
    // are not recognized by the TTS. Therefore we have to explicitly create an sr-only span
    // with the content as a sibling.
    var input_nodes = document.querySelectorAll('input[aria-label], textarea[aria-label]')
    Array.from(input_nodes).forEach( node => {
      // Extract the aria-label attribute value from node
      const ariaLabel = node.getAttribute('aria-label');

      if (ariaLabel && ariaLabel.trim()) {
        // Check if a label DOM already exists as next sibling
        const nextSibling = node.nextSibling;
        const labelExists = nextSibling &&
            nextSibling.tagName &&
            nextSibling.tagName.toLowerCase() === 'span' &&
            nextSibling.classList.contains('sr-only');

        if (!labelExists) {
          // Create DOM element next to the node as a sibling with class and content from the aria-label
          const srLabel = document.createElement('span');
          srLabel.className = 'sr-only';
          srLabel.setAttribute('aria-hidden', 'true'); // NVDA/VoiceOver should reads only input label
          srLabel.textContent = ariaLabel;

          // Insert as next sibling
          node.parentNode.insertBefore(srLabel, node.nextSibling);
        }
      }
    });

    // SpeechStream doesn't recognize aria-labels around MathML so we have to add a data-ssml attribute like so:
    // data-ssml='{"sub": {"alias":"aria label"}}'
    document.querySelectorAll('span[aria-label]:has(> mjx-container)').forEach(node => {
      const ariaLabel = node.getAttribute('aria-label');

      if (ariaLabel && ariaLabel.trim()) {
        node.dataset.ssml = JSON.stringify({ sub: { alias: ariaLabel } });
      }
    });

    sspc.addIgnoreListQuerySelector(sspcIgnoreList);
    if (this._oldReadableNode) {
      if (!Array.isArray(this._oldReadableNode)) {
        this._oldReadableNode = [this._oldReadableNode]

      }
      this._oldReadableNode = _.compact(this._oldReadableNode);
      this._oldReadableNode.forEach( node => {
        sspc.removeAllow(node.id ?? node);
      });
    }

    var nodes = this.readableNode;
    if (nodes) {

      if (!Array.isArray(nodes)) {
        nodes = [nodes]
      }


      // This really should not make it so that only the question parts of the assessment work, however it does
      // currently adding or removing the passage iframe does not register with SpeechStream for an unknown reason.
      // Therefore, the only reason this worked previously is because *everything was always readable* even though we tried to
      // specify that only specific parts were.
      if(!this._includePassage) {
        sspc.setIgnoreAll(true);
        sspc.addIgnoreListQuerySelector('.lrn_scrollablepassage, .passageText');
      }

      nodes = _.compact(nodes);

      // MathJax can be partially loaded due to a race condition with the DOM loading and MathJax CONFIG.ready
      const typesetMathJax = () => {
        const MAX_TRY_COUNT = 10;
        const RETRY_DELAY_MS = 500;
        let tryCount = 0;
        let loadingTriggered = false;

        const tryTypesetMathJax = () => {
          if (tryCount >= MAX_TRY_COUNT) {
            console.warn('MathJax was not loaded after max attempts');
            return;
          }

          if (!window.MathJax) {
            console.warn('MathJax is undefined, retrying...');
            return setTimeout(tryTypesetMathJax, RETRY_DELAY_MS);
          }

          // In case it is stuck in Learnosity loading phase, typesetPromise function might not be loaded yet
          if (!MathJax.typesetPromise) {
            console.warn('MathJax was not loaded yet, retrying...');

            // If MathJax is not loaded, we can force loading it again
            // Calling defaultReady will result in typesetPromise being added to MathJax object
            if (!loadingTriggered && MathJax.startup?.defaultReady) {
              loadingTriggered = true;
              MathJax.startup.defaultReady();

              // If defaultReady added typesetPromise to MathJax, we can typeset immediately
              if (MathJax.typesetPromise) {
                // Not returning here, because we want to retry typesetPromise in another iteration
                // to ensure DOM content loading and MathJax processing as much as possible
                MathJax.typesetPromise();
              }
            }

            tryCount++;
            return setTimeout(tryTypesetMathJax, RETRY_DELAY_MS);
          }

          // MathJax is loaded with typesetPromise function, so we can typeset
          MathJax.typesetPromise();
        }

        tryTypesetMathJax();
      }

      typesetMathJax();

      nodes.forEach( node => {
        if (typeof node === 'string') {
          sspc.addAllow(node);
          dataAllowMathML(node, sspc);
        } else if (typeof node === 'object') {
          sspc.addAllow(node.id);
          dataAllowMathML(node.id, sspc);
          sspc.tagSentences(node);
        }
      });

      // This is a (hopefully) temporary fix until speechstream works out a bug on their end where the if the
      // first readable text on screen is set to be ignored, it doesn't read the second piece of readable text.
      var tts_dummy_target = document.querySelectorAll('h3[class="sr-only"]')
      if (tts_dummy_target) {
        tts_dummy_target.forEach( target => {
          if( target.previousSibling && target.previousSibling.nodeType != 1 || ( target.previousSibling.nodeType == 1 && !target.previousSibling.hasAttribute('data-mc-ignore-read')) ) {
            target.insertAdjacentHTML('beforebegin', '<p style="position: absolute; width: 1px;height: 1px;margin: -1px;padding: 0;overflow: hidden;clip: rect(0, 0, 0, 0);border: 0;" data-mc-ignore-read>‎</p>')
          }
        });
      }

      // reset the start point to be at the question
      this._toolbar.domControlTools.setStartPoint(null);

      // Remember, this could be a node or an id string:
      const firstNode = nodes[0];
      this._toolbar.domControlTools.setStartPoint(firstNode.id ?? firstNode);
    }
  }
}

// Takes a string for a node id and a sspc to get an element and specifically data-allows all 'mjx-assistive-mml' nodes underneath it.
function dataAllowMathML (nodeId, sspc) {
  const nodes  = document.querySelectorAll(`#${nodeId} mjx-container`);
  nodes.forEach( n => {
    sspc.addAllow(n);
  });
}


// This object affects a global state, so it only makes sense to have one:
export const StandardOrator = new StandardOratorClass();

window.addEventListener("toolbarLoaderLoaded", (e) => {
  loader = e.detail.Loader;

  //Triggering SpeechStream if this event fired after SpeechStream had tried loading
  StandardOrator.enabled = StandardOrator.enabled
}, false)